
import { GoogleGenAI, Type } from "@google/genai";
import { PokemonCard } from "../types";

// TCGPlayer Card Conditioning Standards (March 2025)
// Source: https://help.tcgplayer.com/hc/en-us/articles/221430307-Card-Conditioning-Overview
const GRADING_KNOWLEDGE_BASE = `
TCGPlayer Card Conditioning Standards - Use this as your grading reference:

NEAR MINT (NM):
- Surface: Minimal to no wear. Slight scuffing up to 31.5mm² acceptable (1 point each).
- Edges: Slight edgewear up to 20mm contiguous acceptable (1 point each).
- Corners: Sharp or minimally worn. Slight whitening acceptable.
- Creasing: None allowed. Slight indent (pinpoint, doesn't show through) acceptable (1 point).
- Overall: Few minimal imperfections. Total points must stay low.

LIGHTLY PLAYED (LP):
- Surface: Minor scuffing allowed (2 points each). Light scratches visible at angle.
- Edges: Minor edgewear 20-80mm contiguous (2 points). Some whitening.
- Corners: Light wear, minor whitening or small dings.
- Creasing: Minor indent up to 4mm² that doesn't show through (2 points).
- Foil: 3+ roller lines in 5mm² area = LP maximum.
- Overall: Noticeable but not severe imperfections.

MODERATELY PLAYED (MP):
- Surface: Moderate scratches, scuffing clearly visible. Clouding on holos.
- Edges: Significant whitening, multiple worn sections.
- Corners: Noticeable wear, dings, or minor creasing at corners.
- Creasing: Light creases that may show through, small bends.
- Overall: Obvious wear from play/handling but structurally sound.

HEAVILY PLAYED (HP):
- Surface: Heavy scratches, significant scuffing, holo damage.
- Edges: Major whitening, fraying, or damage along edges.
- Corners: Heavy wear, creasing, or damage at corners.
- Creasing: Multiple creases, bends visible from both sides.
- Overall: Heavy wear but card is still intact and legible.

DAMAGED (DMG):
- Any structural damage: tears, holes, water damage, major creases.
- Missing pieces, tape residue, writing/marks on card.
- Excessive bending that compromises card integrity.
- Any condition that makes the card unplayable in a sleeve.
`;

export type CardGrade = 'NM' | 'LP' | 'MP' | 'HP' | 'DMG';

export interface CardGradingResult {
  grade: CardGrade;
  gradeFull: string;
  confidence: number;
  surface: { score: number; notes: string };
  edges: { score: number; notes: string };
  corners: { score: number; notes: string };
  centering: { score: number; notes: string };
  defects: string[];
  summary: string;
  marketImpact: string;
}

const ai = new GoogleGenAI({ apiKey: process.env.API_KEY || '' });

export const geminiService = {
  async gradeCardCondition(base64Image: string): Promise<CardGradingResult> {
    const imagePart = {
      inlineData: {
        mimeType: 'image/jpeg',
        data: base64Image,
      },
    };

    const textPart = {
      text: `
        You are an expert Pokemon TCG card grader using TCGPlayer conditioning standards.

        ${GRADING_KNOWLEDGE_BASE}

        Analyze this card image carefully and grade its physical condition.

        Examine:
        1. SURFACE - Look for scratches, scuffs, print lines, holo damage, clouding
        2. EDGES - Check for whitening, chips, wear along all four edges
        3. CORNERS - Inspect each corner for wear, dings, bends, whitening
        4. CENTERING - Evaluate if the card is well-centered (borders even)
        5. DEFECTS - Note any creases, bends, indents, stains, or other issues

        Score each category 1-10 (10=perfect, 1=severely damaged).

        Determine the final grade: NM, LP, MP, HP, or DMG.
        The grade should reflect the WORST aspect of the card (grade to the lowest category).

        Return JSON with your detailed assessment.
      `
    };

    const response = await ai.models.generateContent({
      model: 'gemini-1.5-flash',
      contents: { parts: [imagePart, textPart] },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            grade: { type: Type.STRING, description: "NM, LP, MP, HP, or DMG" },
            gradeFull: { type: Type.STRING, description: "Full grade name e.g. 'Near Mint'" },
            confidence: { type: Type.NUMBER, description: "Confidence 0-100" },
            surface: {
              type: Type.OBJECT,
              properties: {
                score: { type: Type.NUMBER },
                notes: { type: Type.STRING }
              },
              required: ["score", "notes"]
            },
            edges: {
              type: Type.OBJECT,
              properties: {
                score: { type: Type.NUMBER },
                notes: { type: Type.STRING }
              },
              required: ["score", "notes"]
            },
            corners: {
              type: Type.OBJECT,
              properties: {
                score: { type: Type.NUMBER },
                notes: { type: Type.STRING }
              },
              required: ["score", "notes"]
            },
            centering: {
              type: Type.OBJECT,
              properties: {
                score: { type: Type.NUMBER },
                notes: { type: Type.STRING }
              },
              required: ["score", "notes"]
            },
            defects: { type: Type.ARRAY, items: { type: Type.STRING } },
            summary: { type: Type.STRING, description: "Brief summary of condition" },
            marketImpact: { type: Type.STRING, description: "How grade affects market value" }
          },
          required: ["grade", "gradeFull", "confidence", "surface", "edges", "corners", "centering", "defects", "summary", "marketImpact"]
        }
      }
    });

    return JSON.parse(response.text());
  },

  async identifyCardFromImage(base64Image: string): Promise<{ name: string; number?: string; set?: string; language?: string }> {
    const imagePart = {
      inlineData: {
        mimeType: 'image/jpeg',
        data: base64Image,
      },
    };

    const textPart = {
      text: `
        Analyze this Pokemon TCG Card image. It may be in English, Japanese, Korean, or Chinese (Traditional/Simplified).
        
        1. Identify the Language of the card.
        2. Identify the EXACT Pokemon Name. 
           - **CRITICAL:** If the card is in Japanese/Chinese/Korean, you MUST provide the **ENGLISH** name of the Pokemon (e.g., if you see "リザードン", return "Charizard").
        3. Locate and read the Card Number (e.g., "102/165", "098/078", "SV2a").
           - Japanese cards often have set codes like "s8b" or "sv4a". Capture these precisely.
        4. Identify the Set Name or Set Symbol.
        
        Return a JSON object with:
        - name: The English name of the Pokemon.
        - number: The alphanumeric card number string.
        - set: The set name or code.
        - language: The detected language (e.g., "English", "Japanese", "Chinese", "Korean").
      `
    };

    try {
      const response = await ai.models.generateContent({
        model: 'gemini-1.5-flash',
        contents: { parts: [imagePart, textPart] },
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              name: { type: Type.STRING },
              number: { type: Type.STRING },
              set: { type: Type.STRING },
              language: { type: Type.STRING }
            },
            required: ["name"]
          }
        }
      });
      return JSON.parse(response.text());
    } catch (error) {
      console.error("Gemini Identify Error:", error);
      throw error;
    }


  },

  async analyzeCard(card: PokemonCard) {
    // We utilize Google Search to ground the analysis in the latest TCG meta (2024-2025).
    const prompt = `
      Research the current Pokemon TCG competitive meta (2024-2025) for the card "${card.name}" from the set "${card.set.name}".
      
      Card Details:
      - HP: ${card.hp}
      - Type: ${card.types?.join('/')}
      - Attacks: ${card.attacks?.map(a => a.name).join(', ')}
      - Abilities: ${card.abilities?.map(a => a.name).join(', ')}

      Based on search results and card text, provide a strategic breakdown:
      1. competitiveTier: Is it currently played in top decks? (S-Tier, Rogue, etc.)
      2. strengths: Why is it good?
      3. weaknesses: What counters it?
      4. synergySuggestions: Specific cards that are currently played with it.
      5. deckBuildingTip: A pro tip for building around this card.
      6. funFact: A piece of trivia about the card or Pokemon.

      Return strictly valid JSON.
    `;

    const response = await ai.models.generateContent({
      model: "gemini-1.5-flash",
      contents: { parts: [{ text: prompt }] },
      config: {
        tools: [{ google_search: {} }],
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            competitiveTier: { type: Type.STRING },
            strengths: { type: Type.ARRAY, items: { type: Type.STRING } },
            weaknesses: { type: Type.ARRAY, items: { type: Type.STRING } },
            synergySuggestions: { type: Type.ARRAY, items: { type: Type.STRING } },
            deckBuildingTip: { type: Type.STRING },
            funFact: { type: Type.STRING }
          },
          required: ["competitiveTier", "strengths", "weaknesses", "synergySuggestions", "deckBuildingTip", "funFact"]
        }
      }
    });

    return JSON.parse(response.text());
  }
};
